import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Phone, MessageCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = () => {
    const phone = settings?.whatsapp_number?.replace(/[^0-9]/g, '') || '1234567890';
    const message = encodeURIComponent('Hi! I have a question about your products.');
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-28 pb-16 bg-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-terracotta font-mono text-sm uppercase tracking-wider">
            Get in Touch
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-forest mt-2 mb-4">
            Contact Us
          </h1>
          <p className="text-warm-brown text-lg max-w-2xl mx-auto">
            Have questions about our products? We'd love to hear from you.
            Reach out to us through any of the methods below.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Methods */}
          <div className="space-y-8">
            <h2 className="font-heading text-2xl font-semibold text-forest mb-6">
              Ways to Reach Us
            </h2>

            {/* Contact Cards */}
            <div className="grid gap-6">
              {/* WhatsApp */}
              <button
                onClick={handleWhatsApp}
                className="flex items-center gap-4 p-6 bg-sand/50 rounded-2xl border border-warm-brown/10 hover:border-[#25D366]/50 hover:shadow-lg transition-all text-left group"
                data-testid="contact-whatsapp"
              >
                <div className="w-14 h-14 bg-[#25D366]/10 rounded-xl flex items-center justify-center group-hover:bg-[#25D366] transition-colors">
                  <MessageCircle className="h-7 w-7 text-[#25D366] group-hover:text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-forest">WhatsApp</h3>
                  <p className="text-warm-brown">Chat with us instantly</p>
                  <p className="text-sm text-[#25D366] mt-1">
                    {settings?.whatsapp_number || '+1 234 567 890'}
                  </p>
                </div>
              </button>

              {/* Email */}
              <a
                href={`mailto:${settings?.business_email || 'info@reggaeessences.com'}`}
                className="flex items-center gap-4 p-6 bg-sand/50 rounded-2xl border border-warm-brown/10 hover:border-terracotta/50 hover:shadow-lg transition-all"
                data-testid="contact-email"
              >
                <div className="w-14 h-14 bg-terracotta/10 rounded-xl flex items-center justify-center">
                  <Mail className="h-7 w-7 text-terracotta" />
                </div>
                <div>
                  <h3 className="font-semibold text-forest">Email</h3>
                  <p className="text-warm-brown">Send us an email anytime</p>
                  <p className="text-sm text-terracotta mt-1">
                    {settings?.business_email || 'info@reggaeessences.com'}
                  </p>
                </div>
              </a>

              {/* Phone */}
              <a
                href={`tel:${settings?.business_phone || '+1234567890'}`}
                className="flex items-center gap-4 p-6 bg-sand/50 rounded-2xl border border-warm-brown/10 hover:border-gold/50 hover:shadow-lg transition-all"
                data-testid="contact-phone"
              >
                <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center">
                  <Phone className="h-7 w-7 text-gold" />
                </div>
                <div>
                  <h3 className="font-semibold text-forest">Phone</h3>
                  <p className="text-warm-brown">Call us during business hours</p>
                  <p className="text-sm text-gold mt-1">
                    {settings?.business_phone || '+1 234 567 890'}
                  </p>
                </div>
              </a>
            </div>

            {/* Social Links */}
            <div className="pt-6 border-t border-warm-brown/20">
              <h3 className="font-semibold text-forest mb-4">Follow Us</h3>
              <div className="flex gap-4">
                <a
                  href="https://www.facebook.com/share/1Eww5U67cE/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-[#1877F2] text-white rounded-full hover:opacity-90 transition-opacity"
                >
                  Facebook
                </a>
                <a
                  href="https://www.instagram.com/nadineportersoto"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white rounded-full hover:opacity-90 transition-opacity"
                >
                  Instagram
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-sand/50 p-8 rounded-3xl border border-warm-brown/10">
            <h2 className="font-heading text-2xl font-semibold text-forest mb-6">
              Send Us a Message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-forest mb-2">Your Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="rounded-xl border-warm-brown/20 focus:border-forest"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-forest mb-2">Email Address *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="rounded-xl border-warm-brown/20 focus:border-forest"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-forest mb-2">Phone Number</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="rounded-xl border-warm-brown/20 focus:border-forest"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-forest mb-2">Your Message *</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={5}
                  className="rounded-xl border-warm-brown/20 focus:border-forest"
                  placeholder="How can we help you?"
                  data-testid="contact-message-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-forest hover:bg-forest-light text-cream py-6 rounded-full"
                data-testid="contact-submit-btn"
              >
                {loading ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
